// front-end/src/components/content-editor-src/image-editor.js

import {getAuthToken} from '../auth';
import {apiPost, getMediaUrl, extractFilenameFromPath} from '../../utils/api';
import {config} from '../../config/app-config';
import {createModal} from './ui-elements';
import {updatePendingChanges, getFileForChange} from './content-store';
import {determinePageTitle} from '../../utils/page-utils';

export function openImageEditor(element, options = {}) {
    const isGlobal = element.getAttribute('data-global') === 'true';
    const pageTitle = isGlobal ? '' : determinePageTitle(element);
    const tagId = element.getAttribute('data-content');
    const blockName = element.getAttribute('data-block-name') || findBlockName(element);
    const dataAttribute = element.getAttribute('data-attribute') || 'image';

    console.log(`Opening image editor for ${tagId}, determined page: ${pageTitle}`);

    let currentImageSrc = '';
    if (element.tagName.toLowerCase() === 'img') {
        currentImageSrc = element.src;
    }

    const modalContent = `
    <h3>Edit Image</h3>
    ${currentImageSrc ? `<div class="current-image"><img src="${currentImageSrc}" alt="Current image" style="max-width: 100%; max-height: 200px; object-fit: contain;"></div>` : ''}
    <div class="file-input-container">
      <input type="file" id="image-upload" accept="image/*">
      <label for="image-upload" class="file-label">Choose a file</label>
    </div>
    <div class="file-name" id="file-name"></div>
    ${blockName ? `<div class="block-info">Block: ${blockName}</div>` : ''}
    ${pageTitle && pageTitle !== 'Home' ? `<div class="page-info">Page: ${pageTitle}</div>` : ''}
  `;

    const modal = createModal(modalContent);

    // Add styles for page-info
    const pageInfo = modal.element.querySelector('.page-info');
    if (pageInfo) {
        pageInfo.style.cssText = `
      color: #2196F3;
      font-size: 12px;
      margin-top: 5px;
      padding: 6px;
      background-color: #e3f2fd;
      border-radius: 4px;
    `;
    }

    const fileInput = document.getElementById('image-upload');
    const fileName = document.getElementById('file-name');

    fileInput.addEventListener('change', function () {
        if (fileInput.files.length > 0) {
            fileName.textContent = fileInput.files[0].name;
            modal.enableSave();
        } else {
            fileName.textContent = '';
            modal.disableSave();
        }
    });

    modal.on('save', () => {
        if (fileInput.files.length > 0) {
            // ИЗМЕНЕНИЕ: Теперь только сохраняем в pending changes, не загружаем на сервер
            saveImageChangeLocal(element, {
                pageTitle,
                tagId,
                file: fileInput.files[0],
                blockName,
                dataAttribute
            });
        }
        modal.close();
    });
}

// НОВАЯ ФУНКЦИЯ: Локальное сохранение изображения (только в pending changes)
export function saveImageChangeLocal(element, data) {
    console.log(`Saving image change locally for ${data.tagId} on page: ${data.pageTitle}`);

    // Создаем превью изображения
    const file = data.file;
    const reader = new FileReader();

    reader.onload = function(e) {
        const previewUrl = e.target.result;

        // Обновляем pending changes с файлом
        updatePendingChanges('image', data.tagId, {
            pageTitle: data.pageTitle,
            tagId: data.tagId,
            file: file, // ВАЖНО: Сохраняем оригинальный File объект
            fileName: file.name,
            previewUrl: previewUrl,
            element: element,
            blockName: data.blockName,
            dataAttribute: data.dataAttribute || 'image'
        });

        // Показываем превью пользователю
        if (element.tagName.toLowerCase() === 'img') {
            element.src = previewUrl;
            element.classList.add('content-modified');
        } else {
            // Если это не img элемент, создаем img для превью
            const imgElement = document.createElement('img');
            imgElement.src = previewUrl;
            imgElement.alt = data.tagId;
            imgElement.className = element.className + ' content-modified';

            Array.from(element.attributes).forEach(attr => {
                if (attr.name.startsWith('data-')) {
                    imgElement.setAttribute(attr.name, attr.value);
                }
            });

            element.parentNode.replaceChild(imgElement, element);

            // ИСПРАВЛЕНИЕ: Обновляем только element, не трогаем файл
            updatePendingChanges('image', data.tagId, {
                element: imgElement
            }, true);
        }

        // Показываем кнопку publish
        const publishBtn = document.getElementById(config.editor.selectors.publishButton);
        if (publishBtn) {
            publishBtn.style.display = 'block';
        }

        console.log(`Image preview set for ${data.tagId}, file type:`, file.constructor.name, 'awaiting publish');
    };

    reader.readAsDataURL(file);
}

// ИЗМЕНЕНИЕ: Теперь эта функция вызывается только из publish manager
export function saveImageChange(element, data) {
    console.log(`Publishing image change for ${data.tagId} on page: ${data.pageTitle}`);

    const token = getAuthToken();
    if (!token) {
        return Promise.reject(new Error('Authentication required'));
    }

    // Проверяем что element существует и является DOM элементом
    if (!element || !element.classList) {
        console.error('Invalid element passed to saveImageChange:', element);
        return Promise.reject(new Error('Invalid element'));
    }

    const formData = new FormData();
    formData.append('file', data.file);
    formData.append('page_title', data.pageTitle || '');
    formData.append('tag_id', data.tagId);
    formData.append('tag', data.tagId);
    formData.append('order', 1);
    formData.append('data_attribute', data.dataAttribute || 'image');

    if (data.blockName) {
        formData.append('block_name', data.blockName);
    }

    // ДЕБАГ: Логируем что отправляем
    console.log('FormData содержимое:');
    for (let [key, value] of formData.entries()) {
        if (value instanceof File) {
            console.log(`${key}: File(${value.name}, ${value.type}, ${value.size} bytes)`);
        } else {
            console.log(`${key}:`, value);
        }
    }

    // ДОПОЛНИТЕЛЬНАЯ ПРОВЕРКА: Убеждаемся что file действительно является File объектом
    if (!(data.file instanceof File)) {
        console.error('data.file is not a File object:', data.file);
        console.error('data.file type:', typeof data.file);
        console.error('data.file constructor:', data.file?.constructor?.name);
        return Promise.reject(new Error('Invalid file object'));
    }

    element.classList.add('loading');
    if (element.tagName.toLowerCase() === 'img') {
        element.style.opacity = '0.5';
    }

    return apiPost(config.api.paths.contentImage, formData)
        .then(result => {
            console.log(`✅ Image uploaded successfully for ${data.tagId} on page ${data.pageTitle}`);

            if (result && result.media_path) {
                const imageUrl = getMediaUrlFromPath(result.media_path);

                // Заменяем превью на финальный URL
                if (element.tagName.toLowerCase() === 'img') {
                    element.src = imageUrl;
                    element.style.opacity = '1';
                    element.classList.remove('content-modified', 'loading');
                    element.setAttribute('data-original-src', imageUrl);
                }

                return result;
            }
        })
        .catch(error => {
            console.error(`❌ Error uploading image for ${data.tagId}:`, error);
            if (element.classList) {
                element.classList.remove('loading');
            }
            if (element.tagName.toLowerCase() === 'img') {
                element.style.opacity = '1';
            }
            throw error;
        });
}

export async function publishImageChange(tagId, change) {
    try {
        // Находим актуальный элемент в DOM
        let targetElement = change.element;

        // Если элемент недоступен, ищем по tagId
        if (!targetElement || !targetElement.classList) {
            targetElement = document.querySelector(`[data-content="${tagId}"]`);
        }

        if (!targetElement) {
            throw new Error(`Element with tagId ${tagId} not found`);
        }

        // ИСПРАВЛЕНИЕ: Получаем файл из отдельного хранилища
        const file = getFileForChange('image', tagId);

        if (!file || !(file instanceof File)) {
            console.error('Invalid file for tagId:', tagId, 'file:', file);
            throw new Error(`Invalid file for ${tagId}: expected File object, got ${typeof file}`);
        }

        console.log(`Publishing image ${tagId}, file:`, file.name, file.type, file.size, 'bytes');

        // Создаем новый объект change с правильным файлом
        const changeWithFile = {
            ...change,
            file: file
        };

        // Теперь публикуем файл на сервер
        const result = await saveImageChange(targetElement, changeWithFile);

        if (targetElement.classList) {
            targetElement.classList.remove('content-modified');
        }

        return tagId;
    } catch (error) {
        console.error(`Failed to publish image change for ${tagId}:`, error);
        throw error;
    }
}

function findBlockName(element) {
    let parent = element.parentElement;
    while (parent) {
        if (parent.hasAttribute('data-block-name')) {
            return parent.getAttribute('data-block-name');
        }
        parent = parent.parentElement;
    }
    return '';
}

function getMediaUrlFromPath(mediaPath) {
    if (!mediaPath) return '';

    if (mediaPath.startsWith('/uploads/')) {
        return getMediaUrl(mediaPath.substring(9));
    }

    return getMediaUrl(mediaPath);
}